import { documentServiceClient } from './client';
import { Endpoints } from 'utils/endpoints';
import type { SectionContentType } from 'components/editor/Sections/Section.model';

export type ImportPdfIntoDocumentParams = {
  contentId: string;
  presignedUrl: string;
  filename: string;
  afterSectionId?: string;
  traceparent?: string;
};

type RawPdfImportBlock = {
  id: string;
  type: string;
  topPx?: number;
  leftPx?: number;
  widthPx?: number;
  heightPx?: number;
  zIndex?: number;
  content?: { src?: string };
  styles?: Record<string, unknown>;
};

type RawPdfImportSection = {
  id: string;
  name: string;
  data?: {
    section?: {
      title?: string;
      blocks?: RawPdfImportBlock[];
    };
  };
};

type RawImportPdfIntoDocumentResponse = {
  sections: RawPdfImportSection[];
};

export type ImportPdfIntoDocumentResponse = {
  sections: SectionContentType[];
};

function mapRawSectionToSectionContentType(raw: RawPdfImportSection): SectionContentType {
  const rawBlocks = raw.data?.section?.blocks ?? [];

  const blocks = rawBlocks.map((block) => ({
    blockId: block.id,
    type: block.type,
    zIndex: block.zIndex ?? 0,
    position: {
      left_px: block.leftPx ?? 0,
      top_px: block.topPx ?? 0,
    },
    dimensions: {
      width_px: block.widthPx ?? 0,
      height_px: block.heightPx ?? 0,
    },
    htmlContent: block.content?.src ?? '',
    content: block.content,
    blockSettings: block.styles,
  }));

  return {
    id: raw.id,
    title: raw.data?.section?.title ?? raw.name,
    blocks,
  } as unknown as SectionContentType;
}

export async function importPdfIntoDocument({
  contentId,
  presignedUrl,
  filename,
  afterSectionId,
  traceparent,
}: ImportPdfIntoDocumentParams): Promise<ImportPdfIntoDocumentResponse> {
  const raw = await documentServiceClient.patch<RawImportPdfIntoDocumentResponse>(
    Endpoints.pdfImportIntoDocument(contentId),
    {
      presignedUrl,
      filename,
      afterSectionId,
    },
    { headers: { traceparent: traceparent ?? crypto.randomUUID() } }
  );

  return { sections: raw.sections.map(mapRawSectionToSectionContentType) };
}
